import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, CheckCircle, XCircle, Clock, Activity,
  Globe, ChevronRight, RefreshCw, AlertTriangle,
} from 'lucide-react';
import api from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface K6Execution {
  id: string;
  project_id: string | null;
  capture_id: string;
  skope_scan_id: string | null;
  network_profile: string;
  vus: number;
  duration_seconds: number;
  target_url: string | null;
  status: 'running' | 'passed' | 'failed' | 'error';
  thresholds_passed: boolean;
  p50_ms: number | null;
  p90_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  avg_ms: number | null;
  max_ms: number | null;
  rps: number | null;
  total_requests: number | null;
  error_rate: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PROFILE_LABELS: Record<string, string> = {
  desktop: 'Desktop',
  '4g': '4G LTE',
  '3g': '3G',
  '2g': '2G',
};

const STATUS_CONFIG = {
  passed:  { label: 'Passed',  icon: CheckCircle,  cls: 'text-emerald-400 bg-emerald-400/10' },
  failed:  { label: 'Failed',  icon: XCircle,      cls: 'text-red-400 bg-red-400/10' },
  running: { label: 'Running', icon: Activity,     cls: 'text-blue-400 bg-blue-400/10' },
  error:   { label: 'Error',   icon: AlertTriangle, cls: 'text-yellow-400 bg-yellow-400/10' },
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtMs(ms: number | null): string {
  if (ms == null) return '—';
  return `${ms.toFixed(0)} ms`;
}

function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function hostname(url: string | null): string {
  if (!url) return '—';
  try { return new URL(url).hostname; } catch { return url; }
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PerformanceDashboard() {
  const navigate = useNavigate();
  const [executions, setExecutions] = useState<K6Execution[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [page, setPage]             = useState(1);
  const [total, setTotal]           = useState(0);
  const PAGE_SIZE = 20;

  async function load(p = 1) {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listK6Executions({ page: p, page_size: PAGE_SIZE });
      setExecutions(res.items ?? []);
      setTotal(res.total ?? 0);
      setPage(p);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Error al cargar ejecuciones');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // ── KPIs ────────────────────────────────────────────────────────────────────
  const passed     = executions.filter(e => e.status === 'passed').length;
  const failed     = executions.filter(e => e.status === 'failed' || e.status === 'error').length;
  const passRate   = executions.length ? Math.round((passed / executions.length) * 100) : 0;
  const avgP95     = executions.length
    ? executions.reduce((s, e) => s + (e.p95_ms ?? 0), 0) / executions.filter(e => e.p95_ms != null).length
    : 0;

  return (
    <div className="min-h-screen bg-background-dark text-white p-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-500/10 rounded-lg">
            <Zap className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Performance K6</h1>
            <p className="text-sm text-slate-400">Historial de pruebas de carga</p>
          </div>
        </div>
        <button
          onClick={() => load(page)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white
                     bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Total Ejecuciones</p>
          <p className="text-3xl font-bold text-violet-400">{total}</p>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Pass Rate</p>
          <p className={`text-3xl font-bold ${passRate >= 80 ? 'text-emerald-400' : passRate >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
            {passRate}%
          </p>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">P95 Promedio</p>
          <p className="text-3xl font-bold text-white">
            {avgP95 ? `${avgP95.toFixed(0)}` : '—'}
            {avgP95 ? <span className="text-lg font-normal text-slate-400"> ms</span> : null}
          </p>
        </div>
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Con Fallos</p>
          <p className={`text-3xl font-bold ${failed > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{failed}</p>
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3 text-slate-400">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Cargando ejecuciones...</span>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      ) : executions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="p-4 bg-slate-800 rounded-full mb-4">
            <Zap className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Sin ejecuciones aún</h3>
          <p className="text-slate-400 text-sm max-w-sm mb-4">
            Ejecuta una prueba de carga con el CLI de skope para ver los resultados aquí.
          </p>
          <code className="text-xs bg-slate-800 text-violet-300 px-3 py-2 rounded-lg">
            k6 run --vus 10 --duration 60s script.k6.js
          </code>
        </div>
      ) : (
        <>
          {/* ── Table ── */}
          <div className="bg-surface-dark border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">URL</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Perfil</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">VUs</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Duración</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">P95</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Avg</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Error %</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Estado</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {executions.map(ex => {
                  const st = STATUS_CONFIG[ex.status] ?? STATUS_CONFIG.error;
                  const StatusIcon = st.icon;
                  const errPct = ex.error_rate != null ? (ex.error_rate * 100).toFixed(1) : null;
                  return (
                    <tr
                      key={ex.id}
                      onClick={() => navigate(`/performance/executions/${ex.id}`)}
                      className="hover:bg-slate-800/40 cursor-pointer transition-colors"
                    >
                      {/* URL */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                          <span className="text-sm text-white font-medium truncate max-w-[200px]">
                            {hostname(ex.target_url)}
                          </span>
                        </div>
                        {ex.target_url && (
                          <p className="text-xs text-slate-500 truncate max-w-[200px] pl-5">
                            {ex.target_url}
                          </p>
                        )}
                      </td>
                      {/* Perfil */}
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded-md">
                          {PROFILE_LABELS[ex.network_profile] ?? ex.network_profile}
                        </span>
                      </td>
                      {/* VUs */}
                      <td className="px-4 py-3 text-center text-sm text-white font-mono">
                        {ex.vus}
                      </td>
                      {/* Duración */}
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1 text-sm text-slate-300">
                          <Clock className="w-3.5 h-3.5 text-slate-500" />
                          {fmtDuration(ex.duration_seconds)}
                        </div>
                      </td>
                      {/* P95 */}
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-semibold font-mono ${
                          ex.p95_ms == null ? 'text-slate-500' :
                          ex.p95_ms < 500 ? 'text-emerald-400' :
                          ex.p95_ms < 2000 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {fmtMs(ex.p95_ms)}
                        </span>
                      </td>
                      {/* Avg */}
                      <td className="px-4 py-3 text-right text-sm text-slate-300 font-mono">
                        {fmtMs(ex.avg_ms)}
                      </td>
                      {/* Error % */}
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-mono ${
                          errPct == null ? 'text-slate-500' :
                          parseFloat(errPct) === 0 ? 'text-emerald-400' :
                          parseFloat(errPct) < 5 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {errPct != null ? `${errPct}%` : '—'}
                        </span>
                      </td>
                      {/* Estado */}
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium ${st.cls}`}>
                          <StatusIcon className="w-3 h-3" />
                          {st.label}
                        </span>
                      </td>
                      {/* Fecha */}
                      <td className="px-4 py-3 text-right text-xs text-slate-500">
                        {fmtDate(ex.created_at)}
                      </td>
                      {/* Arrow */}
                      <td className="px-4 py-3 text-slate-600">
                        <ChevronRight className="w-4 h-4" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-400">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total} ejecuciones
              </p>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => load(page - 1)}
                  className="px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg
                             disabled:opacity-40 hover:bg-slate-700 transition-colors"
                >
                  Anterior
                </button>
                <button
                  disabled={page * PAGE_SIZE >= total}
                  onClick={() => load(page + 1)}
                  className="px-3 py-1.5 text-sm bg-slate-800 text-slate-300 rounded-lg
                             disabled:opacity-40 hover:bg-slate-700 transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
